/**
 * Historial de avisos de recogida al cliente (se guarda en este navegador).
 *
 * El backend todavia no registra los avisos: /purchase_order_lines_clients solo
 * devuelve el nombre del cliente y no hay endpoint de notificacion. Hasta que lo
 * haya, el historial vive aqui, por caja. PurchaseOrdersView recarga los pedidos
 * desde la API en cada cambio de filtro, asi que un historial en memoria no
 * sobreviviria ni a eso.
 */
export const NOTIFICATIONS_KEY = 'tpv_order_notifications';

/** Canales tal y como los rotula el TPV de referencia (tpv-albatross-4). */
export const LINE_CHANNEL = 'WhatsApp / Email POS';
export const ORDER_CHANNEL = 'WhatsApp / Email POS (Aviso Pedido Completo)';
/** Envio del resguardo del pedido; se completa con la direccion de destino. */
export const DOCUMENT_CHANNEL = 'Resguardo por email';

export interface NotificationEntry {
    /** ISO 8601: JSON.parse no reconstruye un Date. */
    timestamp: string;
    channel: string;
    /** El correo no salio: consta como intento, pero no cuenta como aviso. */
    failed?: boolean;
}

/** Un intento de aviso tal y como lo guarda el backend. */
export interface NoticeRow {
    /** '' = aviso del pedido completo. */
    lineId: string;
    type: string;
    recipient: string;
    failed: boolean;
    error: string;
    createdAt: string;
}

interface OrderNotifications {
    /** Avisos de "pedido completo". */
    order: NotificationEntry[];
    /** lineId -> avisos de esa linea. */
    lines: Record<string, NotificationEntry[]>;
    /**
     * Envios del resguardo del pedido al cliente. Van en su propio apartado y no
     * con los avisos: mandar el resguardo no es avisar de que el libro ha
     * llegado, y si se mezclaran, la campana daria al cliente por avisado.
     *
     * Opcional porque los registros guardados antes de existir no lo traen.
     */
    documents?: NotificationEntry[];
}

export type NotificationLog = Record<string, OrderNotifications>;

const EMPTY: OrderNotifications = { order: [], lines: {}, documents: [] };

export const readNotificationLog = (): NotificationLog => {
    try {
        const raw = localStorage.getItem(NOTIFICATIONS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed as NotificationLog : {};
    } catch {
        return {};
    }
};

const save = (log: NotificationLog): void => {
    try {
        localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(log));
    } catch { /* sin persistencia: el historial se olvida al recargar */ }
};

export const getOrderNotifications = (log: NotificationLog, orderId: string): NotificationEntry[] =>
    (log[orderId] || EMPTY).order;

/** Envios del resguardo de un pedido. */
export const getDocumentNotifications = (log: NotificationLog, orderId: string): NotificationEntry[] =>
    (log[orderId] || EMPTY).documents || [];

export const getLineNotifications = (
    log: NotificationLog,
    orderId: string,
    lineId: string
): NotificationEntry[] => (log[orderId] || EMPTY).lines[lineId] || [];

const withEntry = (
    log: NotificationLog,
    orderId: string,
    channel: string,
    target: { lineId?: string; document?: boolean } = {}
): NotificationLog => {
    const current = log[orderId] || EMPTY;
    const entry: NotificationEntry = { timestamp: new Date().toISOString(), channel };
    // Los tres apartados se arrastran siempre: antes se perdia el que no tocaba.
    const base: OrderNotifications = {
        order: current.order,
        lines: current.lines,
        documents: current.documents || []
    };

    let updated: OrderNotifications;
    if (target.lineId) {
        updated = { ...base, lines: { ...base.lines, [target.lineId]: [...(base.lines[target.lineId] || []), entry] } };
    } else if (target.document) {
        updated = { ...base, documents: [...base.documents, entry] };
    } else {
        updated = { ...base, order: [...base.order, entry] };
    }

    const next = { ...log, [orderId]: updated };
    save(next);
    return next;
};

/** Registra un aviso de una linea y devuelve el log nuevo, ya persistido. */
export const addLineNotification = (
    log: NotificationLog,
    orderId: string,
    lineId: string,
    channel: string = LINE_CHANNEL
): NotificationLog => withEntry(log, orderId, channel, { lineId });

/** Registra un aviso de pedido completo y devuelve el log nuevo, ya persistido. */
export const addOrderNotification = (
    log: NotificationLog,
    orderId: string,
    channel: string = ORDER_CHANNEL
): NotificationLog => withEntry(log, orderId, channel);

/** Registra el envio del resguardo del pedido al cliente. */
export const addDocumentNotification = (
    log: NotificationLog,
    orderId: string,
    email: string
): NotificationLog => withEntry(log, orderId, `${DOCUMENT_CHANNEL} a ${email}`, { document: true });

/**
 * Fecha de Postgres ("2026-10-28 09:57:17.435-07") a ISO. Sin la T y con el
 * desfase sin minutos, algunos navegadores no la leen; si aun asi falla, se
 * usa la hora actual en vez de romper el historial.
 */
const serverDate = (raw: string): string => {
    const iso = raw.replace(' ', 'T').replace(/([+-]\d\d)$/, '$1:00');
    const d = new Date(iso);
    return (isNaN(d.getTime()) ? new Date() : d).toISOString();
};

/** Si algun intento llego a salir. Los fallidos no cuentan como "ya avisado". */
export const hasBeenNotified = (entries: NotificationEntry[]): boolean => entries.some(e => !e.failed);

/**
 * Sustituye los avisos y las lineas de un pedido por lo que dice el backend. En
 * memoria: no escribe en localStorage. Los resguardos siguen siendo locales.
 */
export const withServerRows = (log: NotificationLog, orderId: string, rows: NoticeRow[]): NotificationLog => {
    const order: NotificationEntry[] = [];
    const lines: Record<string, NotificationEntry[]> = {};

    // El backend los da del mas reciente al mas antiguo; el historial, al reves.
    rows.slice().reverse().forEach(r => {
        if (r.type === 'document') return;
        const entry: NotificationEntry = {
            timestamp: serverDate(r.createdAt),
            channel: `Email a ${r.recipient}${r.lineId ? '' : ' (Aviso Pedido Completo)'}`
                + (r.failed ? ` — no enviado${r.error ? `: ${r.error}` : ''}` : ''),
            failed: r.failed
        };
        if (r.lineId) {
            lines[r.lineId] = [...(lines[r.lineId] || []), entry];
        } else {
            order.push(entry);
        }
    });

    const current = log[orderId] || EMPTY;
    return { ...log, [orderId]: { order, lines, documents: current.documents || [] } };
};
